import { createUseNotifications } from '@gokrew/ui';
import { QueryKeys } from '@gokrew/models/constants';
import { NotificationCategory } from '@gokrew/models/notifications';
// The timesheet keys still live in the app's own enum, which is what
// `useTimesheet` invalidates against.
import { QueryKeys as AppQueryKeys } from '@/constants/query-keys';
import {
  deleteNotification,
  getNotifications,
  getNotificationsUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  openNotificationStream,
  runNotificationAction,
} from '@/api/notifications';
import { useAuth } from '@/context/AuthContext';

/**
 * The company notification routes are company-scoped, so every endpoint is
 * bound to the active company here — `createApi` is re-run whenever it changes,
 * which is also what re-keys the cache and reopens the stream on a switch.
 */
export const notificationHooks = createUseNotifications({
  useCompanyId: () => useAuth().companyId,
  // An inline approve/reject runs the request the item carries instead of the
  // domain's own mutation hook, so the screens it changes are invalidated here.
  // Each list mirrors what that domain's approve mutation already invalidates.
  actionInvalidationKeys: {
    [NotificationCategory.LEAVE_MANAGEMENT]: [
      [QueryKeys.LeaveRequestsManagement],
      [QueryKeys.LeaveRequestsSummary],
      [QueryKeys.CompanyLeaveCalendar],
    ],
    [NotificationCategory.TASKS]: [
      [QueryKeys.TaskAssignmentDetail],
      [QueryKeys.TaskOccurrenceAssignments],
      [QueryKeys.TaskOccurrenceSummary],
      [QueryKeys.TaskOccurrenceActivity],
    ],
    [NotificationCategory.TIMESHEETS]: [
      [AppQueryKeys.TimesheetRequests],
      [AppQueryKeys.TimesheetEmployeesWeek],
      [AppQueryKeys.TimesheetEmployeeRange],
    ],
  },
  createApi: (companyId) => ({
    getFeed: (params) => getNotifications(companyId, params),
    getUnreadCount: () => getNotificationsUnreadCount(companyId),
    markRead: (id) => markNotificationRead(companyId, id),
    markAllRead: () => markAllNotificationsRead(companyId),
    remove: (id) => deleteNotification(companyId, id),
    runAction: runNotificationAction,
    openStream: (signal, handlers) =>
      openNotificationStream(companyId, signal, handlers),
  }),
});

export const {
  useNotificationsUnreadCount,
  useNotificationFeed,
  useNotificationMutations,
  useNotificationStream,
  useRefreshNotifications,
} = notificationHooks;
